import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileUp,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
  UserPlus,
  XCircle,
} from "lucide-react";
import { EVIDENCE_TYPES, PARTICIPANT_TYPES, type ParticipantType } from "@academichub/shared";
import { ApiError } from "../api/client";
import {
  addParticipant,
  archiveActivity,
  deleteActivity,
  deleteEvidence,
  fetchEvidenceBlob,
  getActivity,
  removeParticipant,
  reviewActivity,
  reviseActivity,
  saveBlob,
  submitActivity,
  uploadEvidence,
  validateActivity,
} from "../api/activities";
import { useAuth } from "../hooks/useAuth";
import {
  formatBytes,
  formatDate,
  formatDateTime,
  formatTimeRange,
  participantTypeLabel,
  semesterTypeLabel,
} from "../lib/format";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
  Textarea,
  ValidationBadge,
} from "../components/ui";

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Tindakan gagal dijalankan. Coba lagi.";
}

export function ActivityDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [participantRole, setParticipantRole] = useState("Anggota");
  const [participantType, setParticipantType] = useState<ParticipantType>("MAHASISWA");
  const [evidenceType, setEvidenceType] = useState<string>(EVIDENCE_TYPES[0] ?? "Dokumentasi");
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);

  const detailQuery = useQuery({
    queryKey: ["activity", id],
    queryFn: () => getActivity(id),
    enabled: Boolean(id),
  });

  const activity = detailQuery.data;

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ["activity", id] });
    void queryClient.invalidateQueries({ queryKey: ["activities"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  }

  function onSuccessMessage(message: string) {
    return (): void => {
      setError(null);
      setNotice(message);
      refresh();
    };
  }

  const onError = (caught: unknown): void => {
    setNotice(null);
    setError(messageOf(caught));
  };

  const submitMutation = useMutation({
    mutationFn: () => submitActivity(id),
    onSuccess: onSuccessMessage("Kegiatan diajukan untuk validasi."),
    onError,
  });
  const reviewMutation = useMutation({
    mutationFn: () => reviewActivity(id, notes || undefined),
    onSuccess: onSuccessMessage("Kegiatan ditandai sedang ditelaah."),
    onError,
  });
  const approveMutation = useMutation({
    mutationFn: () => validateActivity(id, "APPROVED", notes || undefined),
    onSuccess: onSuccessMessage("Kegiatan disetujui."),
    onError,
  });
  const rejectMutation = useMutation({
    mutationFn: () => validateActivity(id, "REJECTED", notes),
    onSuccess: () => {
      setRejectOpen(false);
      setNotes("");
      onSuccessMessage("Kegiatan ditolak dengan catatan.")();
    },
    onError,
  });
  const archiveMutation = useMutation({
    mutationFn: () => archiveActivity(id),
    onSuccess: onSuccessMessage("Kegiatan diarsipkan."),
    onError,
  });
  const reviseMutation = useMutation({
    mutationFn: () => reviseActivity(id),
    onSuccess: onSuccessMessage("Kegiatan dikembalikan menjadi draf untuk direvisi."),
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteActivity(id),
    onSuccess: () => {
      refresh();
      navigate("/kegiatan", { replace: true });
    },
    onError,
  });
  const addParticipantMutation = useMutation({
    mutationFn: () =>
      addParticipant(id, {
        name: participantName,
        roleInActivity: participantRole,
        participantType,
      }),
    onSuccess: () => {
      setParticipantName("");
      onSuccessMessage("Peserta ditambahkan.")();
    },
    onError,
  });
  const removeParticipantMutation = useMutation({
    mutationFn: (participantId: string) => removeParticipant(id, participantId),
    onSuccess: onSuccessMessage("Peserta dihapus."),
    onError,
  });
  const uploadEvidenceMutation = useMutation({
    mutationFn: () => {
      if (!evidenceFile) throw new Error("Pilih berkas terlebih dahulu.");
      return uploadEvidence(id, {
        file: evidenceFile,
        evidenceType,
        description: evidenceDescription || undefined,
      });
    },
    onSuccess: () => {
      setEvidenceFile(null);
      setEvidenceDescription("");
      onSuccessMessage("Bukti berhasil diunggah.")();
    },
    onError,
  });
  const deleteEvidenceMutation = useMutation({
    mutationFn: (evidenceId: string) => deleteEvidence(evidenceId),
    onSuccess: onSuccessMessage("Bukti dihapus."),
    onError,
  });
  const downloadEvidenceMutation = useMutation({
    mutationFn: async (evidenceId: string) => {
      const blob = await fetchEvidenceBlob(evidenceId);
      const item = activity?.evidenceItems.find((evidence) => evidence.id === evidenceId);
      saveBlob(blob, item?.originalName ?? "bukti");
    },
    onError,
  });

  if (detailQuery.isLoading) return <Spinner label="Memuat detail kegiatan..." />;
  if (detailQuery.isError || !activity) {
    return (
      <div className="space-y-4">
        <Alert tone="error">
          Kegiatan tidak ditemukan atau berada di luar lingkup wewenang Anda.
        </Alert>
        <Link to="/kegiatan">
          <Button variant="secondary">
            <ArrowLeft className="size-4" aria-hidden />
            Kembali ke daftar
          </Button>
        </Link>
      </div>
    );
  }

  const permissions = activity.permissions;
  const busy =
    submitMutation.isPending ||
    reviewMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    archiveMutation.isPending ||
    reviseMutation.isPending ||
    deleteMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/kegiatan" className="text-sm font-medium text-brand-700 hover:underline">
          <span className="inline-flex items-center gap-1">
            <ArrowLeft className="size-4" aria-hidden />
            Daftar kegiatan
          </span>
        </Link>
        <span className="font-mono text-xs text-slate-500">{activity.activityCode}</span>
      </div>

      <PageHeader
        title={activity.title}
        description={`${activity.studyProgram.name} · ${activity.category.name}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <StatusBadge status={activity.status} />
            <ValidationBadge status={activity.validationStatus} />
          </div>
        }
      />

      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      <Card className="flex flex-wrap gap-2">
        {permissions.canEdit ? (
          <Link to={`/kegiatan/${activity.id}/ubah`}>
            <Button variant="secondary">
              <Pencil className="size-4" aria-hidden />
              Ubah
            </Button>
          </Link>
        ) : null}
        {permissions.canSubmit ? (
          <Button loading={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
            <Send className="size-4" aria-hidden />
            Ajukan
          </Button>
        ) : null}
        {permissions.canReview ? (
          <Button
            variant="secondary"
            loading={reviewMutation.isPending}
            onClick={() => reviewMutation.mutate()}
          >
            Tandai ditelaah
          </Button>
        ) : null}
        {permissions.canValidate ? (
          <>
            <Button
              loading={approveMutation.isPending}
              disabled={busy}
              onClick={() => approveMutation.mutate()}
            >
              <CheckCircle2 className="size-4" aria-hidden />
              Setujui
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => setRejectOpen(true)}>
              <XCircle className="size-4" aria-hidden />
              Tolak
            </Button>
          </>
        ) : null}
        {activity.status === "REJECTED" ? (
          <Button
            variant="secondary"
            loading={reviseMutation.isPending}
            onClick={() => reviseMutation.mutate()}
          >
            <RotateCcw className="size-4" aria-hidden />
            Revisi
          </Button>
        ) : null}
        {permissions.canArchive ? (
          <Button
            variant="secondary"
            loading={archiveMutation.isPending}
            onClick={() => archiveMutation.mutate()}
          >
            <Archive className="size-4" aria-hidden />
            Arsipkan
          </Button>
        ) : null}
        {activity.submittedById === user?.id && activity.status === "DRAFT" ? (
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => {
              if (window.confirm("Hapus draf kegiatan ini?")) deleteMutation.mutate();
            }}
          >
            <Trash2 className="size-4" aria-hidden />
            Hapus
          </Button>
        ) : null}
      </Card>

      {activity.validationNotes ? (
        <Alert tone="info">Catatan validator: {activity.validationNotes}</Alert>
      ) : null}

      <Card className="space-y-4">
        <h2 className="font-semibold text-slate-800">Informasi kegiatan</h2>
        <p className="text-sm text-slate-700">{activity.description}</p>
        <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Info label="Tanggal" value={formatDate(activity.activityDate)} />
          <Info label="Waktu" value={formatTimeRange(activity.startTime, activity.endTime)} />
          <Info label="Lokasi" value={activity.location} />
          <Info label="Penyelenggara" value={activity.organizer} />
          <Info label="Penanggung jawab" value={activity.responsiblePerson} />
          <Info label="Semester" value={semesterTypeLabel(activity.semester.semesterType)} />
          <Info label="Tahun akademik" value={activity.academicYear.name} />
          <Info label="Fakultas" value={activity.faculty.name} />
          <Info label="Program studi" value={activity.studyProgram.name} />
          <Info label="Dicatat oleh" value={activity.submittedBy.fullName} />
          <Info label="Luaran" value={activity.output ?? "-"} />
          <Info label="Hasil" value={activity.outcome ?? "-"} />
          <Info label="Tautan" value={activity.externalLink ?? "-"} />
          <Info
            label="Divalidasi"
            value={
              activity.validatedAt ? `${formatDateTime(activity.validatedAt)}` : "Belum divalidasi"
            }
          />
        </dl>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold text-slate-800">Peserta ({activity.participants.length})</h2>
        {activity.participants.length === 0 ? (
          <EmptyState title="Belum ada peserta tercatat" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {activity.participants.map((participant) => (
              <li key={participant.id} className="flex items-center justify-between gap-3 py-2">
                <span>
                  <span className="block text-sm font-medium text-slate-800">
                    {participant.name}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {participant.roleInActivity} ·{" "}
                    {participantTypeLabel(participant.participantType)}
                  </span>
                </span>
                {permissions.canUploadEvidence ? (
                  <Button
                    variant="ghost"
                    onClick={() => removeParticipantMutation.mutate(participant.id)}
                  >
                    Hapus
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {permissions.canUploadEvidence ? (
          <form
            className="grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-4"
            onSubmit={(event) => {
              event.preventDefault();
              addParticipantMutation.mutate();
            }}
          >
            <Field label="Nama peserta" htmlFor="participantName">
              <Input
                id="participantName"
                value={participantName}
                onChange={(event) => setParticipantName(event.target.value)}
                required
              />
            </Field>
            <Field label="Peran" htmlFor="participantRole">
              <Input
                id="participantRole"
                value={participantRole}
                onChange={(event) => setParticipantRole(event.target.value)}
                required
              />
            </Field>
            <Field label="Jenis" htmlFor="participantType">
              <Select
                id="participantType"
                value={participantType}
                onChange={(event) => setParticipantType(event.target.value as ParticipantType)}
              >
                {PARTICIPANT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {participantTypeLabel(type)}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex items-end">
              <Button type="submit" loading={addParticipantMutation.isPending} className="w-full">
                <UserPlus className="size-4" aria-hidden />
                Tambah
              </Button>
            </div>
          </form>
        ) : null}
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold text-slate-800">
          Bukti dukung ({activity.evidenceItems.length})
        </h2>
        {activity.evidenceItems.length === 0 ? (
          <EmptyState title="Belum ada bukti diunggah" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {activity.evidenceItems.map((evidence) => (
              <li
                key={evidence.id}
                className="flex flex-wrap items-center justify-between gap-3 py-2"
              >
                <span>
                  <span className="block text-sm font-medium text-slate-800">
                    {evidence.originalName}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {evidence.evidenceType} · {formatBytes(evidence.fileSize)} ·{" "}
                    {formatDateTime(evidence.uploadedAt)}
                  </span>
                </span>
                <span className="flex gap-2">
                  <Button
                    variant="secondary"
                    loading={downloadEvidenceMutation.isPending}
                    onClick={() => downloadEvidenceMutation.mutate(evidence.id)}
                  >
                    <Download className="size-4" aria-hidden />
                    Unduh
                  </Button>
                  {permissions.canUploadEvidence ? (
                    <Button
                      variant="ghost"
                      onClick={() => deleteEvidenceMutation.mutate(evidence.id)}
                    >
                      Hapus
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}

        {permissions.canUploadEvidence ? (
          <form
            className="grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              uploadEvidenceMutation.mutate();
            }}
          >
            <Field
              label="Berkas"
              htmlFor="evidenceFile"
              hint="JPG, PNG, WEBP, PDF, DOCX (maks 15 MB)"
            >
              <Input
                id="evidenceFile"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
                onChange={(event) => setEvidenceFile(event.target.files?.[0] ?? null)}
                required
              />
            </Field>
            <Field label="Jenis bukti" htmlFor="evidenceType">
              <Select
                id="evidenceType"
                value={evidenceType}
                onChange={(event) => setEvidenceType(event.target.value)}
              >
                {EVIDENCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Keterangan (opsional)" htmlFor="evidenceDescription">
              <Input
                id="evidenceDescription"
                value={evidenceDescription}
                onChange={(event) => setEvidenceDescription(event.target.value)}
              />
            </Field>
            <div className="sm:col-span-3">
              <Button type="submit" loading={uploadEvidenceMutation.isPending}>
                <FileUp className="size-4" aria-hidden />
                Unggah bukti
              </Button>
            </div>
          </form>
        ) : null}
      </Card>

      <Card className="space-y-3">
        <h2 className="font-semibold text-slate-800">Riwayat status</h2>
        <ol className="space-y-3">
          {activity.statusHistory.map((entry) => (
            <li key={entry.id} className="flex gap-3">
              <span className="mt-1 size-2 shrink-0 rounded-full bg-brand-500" />
              <span className="text-sm">
                <span className="font-medium text-slate-800">
                  {entry.fromStatus ? `${entry.fromStatus} → ` : ""}
                  {entry.toStatus}
                </span>
                <span className="block text-xs text-slate-500">
                  {formatDateTime(entry.createdAt)}
                  {entry.notes ? ` · ${entry.notes}` : ""}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </Card>

      <Modal
        open={rejectOpen}
        title="Tolak kegiatan"
        onClose={() => setRejectOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>
              Batal
            </Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              onClick={() => rejectMutation.mutate()}
            >
              Tolak kegiatan
            </Button>
          </>
        }
      >
        <Field label="Alasan penolakan" hint="Minimal 5 karakter." htmlFor="rejectNotes">
          <Textarea
            id="rejectNotes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            required
          />
        </Field>
      </Modal>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}
